import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { UserContext } from "../services/UserContext";
import { useClient, canManageClinica } from "../services/ClientContext";
import { API, authHeaders, resolveStorageUrl } from "../services/config";
import {
  fetchPushStatus,
  getNotificationPermission,
  isIosDevice,
  isPushSupported,
  isStandalonePwa,
  subscribeToPush,
  unsubscribeFromPush,
} from "../services/pushNotifications";
import "./ConfiguracionPage.css";

const NAV_ITEMS_ALL = [
  { id: "membrete", label: "Membrete" },
  { id: "firma", label: "Pie de página" },
  { id: "autogestion", label: "Autogestión" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "notificaciones", label: "Notificaciones" },
];

const NAV_ITEMS_PROFESIONAL = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "notificaciones", label: "Notificaciones" },
];

export default function ConfiguracionPage() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { modulos, multiProfesional } = useClient();
  const contentRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [connectionState, setConnectionState] = useState("");
  const [configured, setConfigured] = useState(false);
  const [instanceName, setInstanceName] = useState("");
  const [managerUrl, setManagerUrl] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [queueStats, setQueueStats] = useState({ pending: 0, processing: 0, sent: 0, failed: 0 });
  const [qr, setQr] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [waMode, setWaMode] = useState("direct");
  const [hubOnline, setHubOnline] = useState(false);

  const [waDisplayName, setWaDisplayName] = useState("");
  const [waContactPhone, setWaContactPhone] = useState("");
  const [waContactName, setWaContactName] = useState("");
  const [waConfigMsg, setWaConfigMsg] = useState({ type: "", text: "" });
  const [savingWaConfig, setSavingWaConfig] = useState(false);
  const [loadingWaConfig, setLoadingWaConfig] = useState(false);
  const [profesionalesWa, setProfesionalesWa] = useState([]);
  const [selectedProfesionalId, setSelectedProfesionalId] = useState(null);

  const [pushSupported] = useState(() => isPushSupported());
  const [pushPermission, setPushPermission] = useState(() => getNotificationPermission());
  const [pushConfigured, setPushConfigured] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushOtherDevices, setPushOtherDevices] = useState(0);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSaving, setPushSaving] = useState(false);
  const [pushMsg, setPushMsg] = useState({ type: "", text: "" });
  const [pushProfesionalId, setPushProfesionalId] = useState(null);

  const [membreteModo, setMembreteModo] = useState("completo");
  const [membreteUrl, setMembreteUrl] = useState(null);
  const [membreteLogoUrl, setMembreteLogoUrl] = useState(null);
  const [membreteLogoFile, setMembreteLogoFile] = useState(null);
  const [membreteTexto, setMembreteTexto] = useState("");
  const [pieModo, setPieModo] = useState("completo");
  const [firmaUrl, setFirmaUrl] = useState(null);
  const [pieFirmaUrl, setPieFirmaUrl] = useState(null);
  const [pieFirmaFile, setPieFirmaFile] = useState(null);
  const [uploadingMembrete, setUploadingMembrete] = useState(false);
  const [savingEncabezado, setSavingEncabezado] = useState(false);
  const [uploadingFirma, setUploadingFirma] = useState(false);
  const [savingPie, setSavingPie] = useState(false);
  const [clinicaMsg, setClinicaMsg] = useState("");
  const [pieMsg, setPieMsg] = useState("");
  const [permiteAutogestion, setPermiteAutogestion] = useState(false);
  const [landingTitulo, setLandingTitulo] = useState("");
  const [landingSubtitulo, setLandingSubtitulo] = useState("");
  const [landingColor, setLandingColor] = useState("#0A1B3D");
  const [waBotPhone, setWaBotPhone] = useState("");
  const [autogestionMsg, setAutogestionMsg] = useState("");
  const [savingAutogestion, setSavingAutogestion] = useState(false);

  const puedeGestionar = canManageClinica(user, multiProfesional);
  const showMembretePie = modulos.pacientes && puedeGestionar;
  const canAccessConfig =
    modulos.configuracion && (puedeGestionar || user?.role === "profesional");
  const navItems = showMembretePie ? NAV_ITEMS_ALL : NAV_ITEMS_PROFESIONAL;

  useEffect(() => {
    if (user && !canAccessConfig) {
      navigate("/", { replace: true });
    }
  }, [user, canAccessConfig, navigate]);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(`config-${sectionId}`);
    if (!el) return;
    const topbarOffset = 16;
    const top = el.getBoundingClientRect().top + window.scrollY - topbarOffset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const fetchConfigClinica = async () => {
    try {
      const res = await fetch(`${API}/configuracion-clinica`, { headers: authHeaders() });
      const json = await res.json();
      if (res.ok && json.success) {
        setMembreteModo(json.data?.membrete_modo || "completo");
        setMembreteUrl(resolveStorageUrl(json.data?.membrete_url));
        setMembreteLogoUrl(resolveStorageUrl(json.data?.membrete_logo_url));
        setMembreteTexto(json.data?.membrete_texto || "");
        setPieModo(json.data?.pie_modo || "completo");
        setFirmaUrl(resolveStorageUrl(json.data?.firma_sello_url));
        setPieFirmaUrl(resolveStorageUrl(json.data?.pie_firma_url));
        setPermiteAutogestion(Boolean(json.data?.permite_autogestion_turnos));
        setLandingTitulo(json.data?.landing_titulo || "");
        setLandingSubtitulo(json.data?.landing_subtitulo || "");
        setLandingColor(json.data?.landing_color_acento || "#0A1B3D");
        setWaBotPhone(json.data?.wa_bot_phone || "");
      }
    } catch {
      /* ignore */
    }
  };

  const myProfesionalId = user?.profesional?.id ?? null;
  const showProfesionalSelector = profesionalesWa.length > 1;

  const profesionalLabel = (p) =>
    [p.apellido, p.nombre].filter(Boolean).join(", ") || p.nombre || `Profesional #${p.id}`;

  useEffect(() => {
    if (!canAccessConfig) return;

    const loadProfesionales = async () => {
      try {
        const res = await fetch(`${API}/profesionales`, { headers: authHeaders() });
        const json = await res.json();
        if (!res.ok || !json.success) return;

        const list = json.data || [];
        setProfesionalesWa(list);

        if (list.length === 0) return;

        const preferred = myProfesionalId && list.some((p) => p.id === myProfesionalId)
          ? myProfesionalId
          : list[0].id;
        setSelectedProfesionalId(preferred);
      } catch {
        /* ignore */
      }
    };

    loadProfesionales();
  }, [canAccessConfig, myProfesionalId]);

  useEffect(() => {
    if (!selectedProfesionalId) return;

    const fetchWaConfig = async () => {
      setLoadingWaConfig(true);
      setWaConfigMsg({ type: "", text: "" });
      try {
        const res = await fetch(`${API}/profesionales/${selectedProfesionalId}`, { headers: authHeaders() });
        const json = await res.json();
        if (res.ok && json.success) {
          setWaDisplayName(json.data?.wa_display_name ?? "");
          setWaContactPhone(json.data?.wa_contact_phone ?? "");
          setWaContactName(json.data?.wa_contact_name ?? "");
        }
      } catch { /* ignore */ }
      finally { setLoadingWaConfig(false); }
    };

    fetchWaConfig();
  }, [selectedProfesionalId]);

  useEffect(() => {
    if (!selectedProfesionalId) return;
    setPushProfesionalId((current) => current || selectedProfesionalId);
  }, [selectedProfesionalId]);

  const reloadPushStatus = async ({ silent = false } = {}) => {
    const profId = pushProfesionalId || selectedProfesionalId || myProfesionalId;
    if (!canAccessConfig || !pushSupported || !profId) return;

    if (!silent) {
      setPushLoading(true);
      setPushMsg({ type: "", text: "" });
    }
    try {
      const data = await fetchPushStatus(profId);
      setPushConfigured(Boolean(data.configured));
      setPushSubscribed(Boolean(data.subscribedOnDevice ?? data.subscribed));
      setPushOtherDevices(Number(data.otherDevicesCount) || 0);
      setPushPermission(getNotificationPermission());
    } catch (err) {
      if (!silent) {
        setPushMsg({ type: "err", text: err.message || "No se pudo consultar notificaciones." });
      }
    } finally {
      if (!silent) {
        setPushLoading(false);
      }
    }
  };

  useEffect(() => {
    reloadPushStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessConfig, pushSupported, pushProfesionalId, selectedProfesionalId, myProfesionalId]);

  const activarPush = async () => {
    const profId = pushProfesionalId || selectedProfesionalId || myProfesionalId;
    if (!profId) {
      setPushMsg({ type: "err", text: "Seleccioná un profesional para recibir alertas." });
      return;
    }

    setPushSaving(true);
    setPushMsg({ type: "", text: "" });
    try {
      await subscribeToPush(profId);
      await reloadPushStatus({ silent: true });
      setPushMsg({
        type: "ok",
        text: "Notificaciones activadas en este dispositivo. Te avisaremos cuando un paciente confirme, cancele o pida contacto.",
      });
    } catch (err) {
      setPushMsg({ type: "err", text: err.message || "No se pudieron activar las notificaciones." });
    } finally {
      setPushSaving(false);
    }
  };

  const desactivarPush = async () => {
    setPushSaving(true);
    setPushMsg({ type: "", text: "" });
    try {
      await unsubscribeFromPush();
      await reloadPushStatus({ silent: true });
      setPushMsg({ type: "ok", text: "Notificaciones desactivadas en este dispositivo." });
    } catch (err) {
      setPushMsg({ type: "err", text: err.message || "No se pudieron desactivar las notificaciones." });
    } finally {
      setPushSaving(false);
    }
  };

  const guardarWaConfig = async () => {
    if (!selectedProfesionalId) return;
    setSavingWaConfig(true);
    setWaConfigMsg({ type: "", text: "" });
    try {
      const res = await fetch(`${API}/profesionales/${selectedProfesionalId}/wa-config`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          wa_display_name:  waDisplayName.trim() || null,
          wa_contact_phone: waContactPhone.trim() || null,
          wa_contact_name:  waContactName.trim() || null,
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setWaConfigMsg({ type: "ok", text: "Configuración guardada correctamente." });
      } else {
        setWaConfigMsg({ type: "err", text: json.message || "Error al guardar." });
      }
    } catch {
      setWaConfigMsg({ type: "err", text: "Error de conexión." });
    } finally {
      setSavingWaConfig(false);
    }
  };

  useEffect(() => {
    const fetchWaState = async () => {
      setLoading(true);
      setError("");
      try {
        const statusRes = await fetch(`${API}/whatsapp/status`, { headers: authHeaders() });
        const statusData = await statusRes.json();

        if (!statusRes.ok || !statusData?.success) {
          setError(statusData?.message || "No se pudo consultar el estado de WhatsApp.");
          return;
        }

        const hubMode = statusData.mode === "hub";
        setConfigured(hubMode ? true : Boolean(statusData.configured));
        setReady(Boolean(statusData.ready));
        setWaMode(hubMode ? "hub" : "direct");
        setHubOnline(Boolean(statusData.hubOnline));
        setConnectionState(statusData.connectionState || "");
        setInstanceName(statusData.instance || "");
        setManagerUrl(statusData.managerUrl || "");
        setPairingCode(statusData.pairingCode || "");
        setQueueStats(statusData.queue || { pending: 0, processing: 0, sent: 0, failed: 0 });
        setQr(statusData.qr || "");
        setError(statusData.error || "");
      } catch {
        setError("No se pudo conectar con el servicio de WhatsApp.");
      } finally {
        setLoading(false);
      }
    };

    fetchWaState();
    if (showMembretePie) {
      fetchConfigClinica();
    }
    const intervalId = window.setInterval(fetchWaState, 10000);
    return () => window.clearInterval(intervalId);
  }, [showMembretePie]);

  const uploadClinica = async (endpoint, file, setUploading) => {
    const body = new FormData();
    body.append("archivo", file);
    setUploading(true);
    setClinicaMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        if (endpoint === "membrete") {
          setMembreteModo(json.data?.membrete_modo || "completo");
          setMembreteUrl(resolveStorageUrl(json.data?.membrete_url));
          setClinicaMsg("Imagen guardada correctamente.");
        } else {
          setPieModo(json.data?.pie_modo || "completo");
          setFirmaUrl(resolveStorageUrl(json.data?.firma_sello_url));
          setPieMsg("Imagen guardada correctamente.");
        }
      } else {
        setClinicaMsg(json.message || "Error al subir la imagen.");
      }
    } catch {
      setClinicaMsg("Error de conexión al subir.");
    } finally {
      setUploading(false);
    }
  };

  const guardarConfiguracionEncabezado = async () => {
    const body = new FormData();
    body.append("modo", membreteModo);
    body.append("texto", membreteTexto);
    if (membreteLogoFile) body.append("logo", membreteLogoFile);

    setSavingEncabezado(true);
    setClinicaMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/encabezado`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setMembreteModo(json.data?.membrete_modo || membreteModo);
        setMembreteLogoUrl(resolveStorageUrl(json.data?.membrete_logo_url));
        setMembreteTexto(json.data?.membrete_texto || "");
        setMembreteLogoFile(null);
        setClinicaMsg("Encabezado guardado correctamente.");
      } else {
        setClinicaMsg(json.message || "Error al guardar el encabezado.");
      }
    } catch {
      setClinicaMsg("Error de conexión al guardar el encabezado.");
    } finally {
      setSavingEncabezado(false);
    }
  };

  const guardarConfiguracionPie = async () => {
    const body = new FormData();
    body.append("modo", pieModo);
    if (pieFirmaFile) body.append("imagen", pieFirmaFile);

    setSavingPie(true);
    setPieMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/pie`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setPieModo(json.data?.pie_modo || pieModo);
        setPieFirmaUrl(resolveStorageUrl(json.data?.pie_firma_url));
        setPieFirmaFile(null);
        setPieMsg("Pie de página guardado correctamente.");
      } else {
        setPieMsg(json.message || "Error al guardar el pie de página.");
      }
    } catch {
      setPieMsg("Error de conexión al guardar el pie de página.");
    } finally {
      setSavingPie(false);
    }
  };

  const guardarAutogestion = async () => {
    setSavingAutogestion(true);
    setAutogestionMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/autogestion`, {
        method: "POST",
        headers: {
          ...authHeaders(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          permite_autogestion_turnos: permiteAutogestion,
          landing_titulo: landingTitulo,
          landing_subtitulo: landingSubtitulo,
          landing_color_acento: landingColor,
          wa_bot_phone: waBotPhone,
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setPermiteAutogestion(Boolean(json.data?.permite_autogestion_turnos));
        setLandingTitulo(json.data?.landing_titulo || "");
        setLandingSubtitulo(json.data?.landing_subtitulo || "");
        setLandingColor(json.data?.landing_color_acento || "#0A1B3D");
        setWaBotPhone(json.data?.wa_bot_phone || "");
        setAutogestionMsg("Configuración de autogestión guardada.");
      } else {
        setAutogestionMsg(json.message || "Error al guardar la autogestión.");
      }
    } catch {
      setAutogestionMsg("Error de conexión al guardar la autogestión.");
    } finally {
      setSavingAutogestion(false);
    }
  };

  return (
    <div className="configuracion-page">
      <Sidebar />
      <main className="configuracion-content" ref={contentRef}>
        <h1>Configuración</h1>

        <nav className="config-nav" aria-label="Ir a sección">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="config-nav-btn"
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {showMembretePie ? (
        <>
        <section id="config-membrete" className="config-section">
          <h2>Membrete (encabezado)</h2>
          <p className="configuracion-subtitle">
            Elegí cómo querés armar el encabezado de las prescripciones impresas.
          </p>

          {clinicaMsg ? <div className="config-alert info">{clinicaMsg}</div> : null}

          <div className="config-header-options">
            <button
              type="button"
              className={`config-option-card ${membreteModo === "completo" ? "active" : ""}`}
              onClick={() => setMembreteModo("completo")}
            >
              <strong>Subir membrete completo</strong>
              <span>Usá una imagen ya diseñada con logo, datos y estilo final.</span>
            </button>
            <button
              type="button"
              className={`config-option-card ${membreteModo === "armado" ? "active" : ""}`}
              onClick={() => setMembreteModo("armado")}
            >
              <strong>Armar con logo + texto</strong>
              <span>Subí el logo y escribí dirección, teléfono, horarios u otros datos.</span>
            </button>
          </div>

          {membreteModo === "completo" ? (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 1: membrete completo</h3>
              <p className="config-muted">
                Recomendado si ya tenés una imagen final lista para ocupar todo el encabezado del PDF.
              </p>
              {membreteUrl ? (
                <img src={membreteUrl} alt="Membrete actual" className="config-preview-img" />
              ) : (
                <p className="config-muted">Sin membrete cargado.</p>
              )}
              <div className="config-actions-row">
              <label className="config-upload-label">
                <input
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="config-file-input"
                  disabled={uploadingMembrete}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadClinica("membrete", f, setUploadingMembrete);
                    e.target.value = "";
                  }}
                />
                {uploadingMembrete ? "Subiendo..." : "Subir membrete completo"}
              </label>
                <button
                  type="button"
                  className="config-save-btn config-save-secondary"
                  onClick={guardarConfiguracionEncabezado}
                  disabled={savingEncabezado}
                >
                  {savingEncabezado ? "Guardando..." : "Usar este modo"}
                </button>
              </div>
            </div>
          ) : (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 2: armar encabezado</h3>
              <p className="config-muted">
                El PDF mostrará el logo centrado, debajo el texto centrado y una línea horizontal para cerrar el encabezado.
              </p>

              <div className="config-built-preview" aria-label="Vista previa del encabezado armado">
                {membreteLogoFile || membreteLogoUrl ? (
                  <img
                    src={membreteLogoFile ? URL.createObjectURL(membreteLogoFile) : membreteLogoUrl}
                    alt="Logo del encabezado"
                    className="config-logo-preview"
                  />
                ) : (
                  <div className="config-logo-placeholder">Logo</div>
                )}
                <p>{membreteTexto || "Dirección, teléfono, horarios, matrícula u otros datos del consultorio"}</p>
                <span />
              </div>

              <label className="config-field-label">Logo del encabezado</label>
              <label className="config-upload-label config-upload-secondary">
                <input
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="config-file-input"
                  disabled={savingEncabezado}
                  onChange={(e) => {
                    setMembreteLogoFile(e.target.files?.[0] || null);
                  }}
                />
                {membreteLogoFile ? membreteLogoFile.name : "Seleccionar logo"}
              </label>

              <label className="config-field-label" htmlFor="config-membrete-texto">
                Texto centrado del encabezado
              </label>
              <textarea
                id="config-membrete-texto"
                className="config-textarea"
                rows={4}
                value={membreteTexto}
                onChange={(e) => setMembreteTexto(e.target.value)}
                placeholder={"Ej: Av. Siempre Viva 123 - Córdoba\nTel: 351 000-0000\nAtención de lunes a viernes"}
              />

              <button
                type="button"
                className="config-save-btn"
                onClick={guardarConfiguracionEncabezado}
                disabled={savingEncabezado}
              >
                {savingEncabezado ? "Guardando..." : "Guardar encabezado armado"}
              </button>
            </div>
          )}
        </section>

        <section id="config-firma" className="config-section">
          <h2>Pie de página (firma y sello)</h2>
          <p className="configuracion-subtitle">
            Opcional. Elegí cómo querés mostrar la firma al imprimir prescripciones.
          </p>

          {pieMsg ? <div className="config-alert info">{pieMsg}</div> : null}

          <div className="config-header-options">
            <button
              type="button"
              className={`config-option-card ${pieModo === "completo" ? "active" : ""}`}
              onClick={() => setPieModo("completo")}
            >
              <strong>Subir imagen completa</strong>
              <span>Usá una imagen ya armada con firma, sello y datos del pie.</span>
            </button>
            <button
              type="button"
              className={`config-option-card ${pieModo === "armado" ? "active" : ""}`}
              onClick={() => setPieModo("armado")}
            >
              <strong>Armar con línea + firma</strong>
              <span>El PDF mostrará una línea horizontal y debajo la imagen de firma o sello.</span>
            </button>
          </div>

          {pieModo === "completo" ? (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 1: pie completo</h3>
              <p className="config-muted">
                Recomendado si ya tenés una imagen final del pie de página lista para el PDF.
              </p>
              {firmaUrl ? (
                <img src={firmaUrl} alt="Pie de página actual" className="config-preview-img config-preview-firma" />
              ) : (
                <p className="config-muted">Sin pie de página cargado.</p>
              )}
              <div className="config-actions-row">
                <label className="config-upload-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    className="config-file-input"
                    disabled={uploadingFirma}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadClinica("firma", f, setUploadingFirma);
                      e.target.value = "";
                    }}
                  />
                  {uploadingFirma ? "Subiendo..." : "Subir pie completo"}
                </label>
                <button
                  type="button"
                  className="config-save-btn config-save-secondary"
                  onClick={guardarConfiguracionPie}
                  disabled={savingPie}
                >
                  {savingPie ? "Guardando..." : "Usar este modo"}
                </button>
              </div>
            </div>
          ) : (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 2: línea + firma o sello</h3>
              <p className="config-muted">
                El PDF dibujará una línea horizontal y, debajo, la imagen que subas (firma, sello o ambos).
              </p>

              <div className="config-built-preview config-built-preview-pie" aria-label="Vista previa del pie de página">
                <span />
                {pieFirmaFile || pieFirmaUrl ? (
                  <img
                    src={pieFirmaFile ? URL.createObjectURL(pieFirmaFile) : pieFirmaUrl}
                    alt="Firma o sello"
                    className="config-logo-preview config-preview-firma"
                  />
                ) : (
                  <div className="config-logo-placeholder">Firma / sello</div>
                )}
              </div>

              <label className="config-field-label">Imagen de firma o sello</label>
              <label className="config-upload-label config-upload-secondary">
                <input
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="config-file-input"
                  disabled={savingPie}
                  onChange={(e) => {
                    setPieFirmaFile(e.target.files?.[0] || null);
                  }}
                />
                {pieFirmaFile ? pieFirmaFile.name : "Seleccionar imagen"}
              </label>

              <button
                type="button"
                className="config-save-btn"
                onClick={guardarConfiguracionPie}
                disabled={savingPie}
              >
                {savingPie ? "Guardando..." : "Guardar pie de página armado"}
              </button>
            </div>
          )}
        </section>
        </>
        ) : null}

        {showMembretePie ? (
        <section id="config-autogestion" className="config-section">
          <h2>Autogestión de turnos</h2>
          <p className="configuracion-subtitle">
            Habilita la landing pública para que los pacientes reserven turno solos.
            Antes debés cargar horarios de atención en Profesionales.
          </p>

          {autogestionMsg ? <div className="config-alert info">{autogestionMsg}</div> : null}

          <div className="config-card config-card-wide">
            <label className="config-toggle-row">
              <input
                type="checkbox"
                checked={permiteAutogestion}
                onChange={(e) => setPermiteAutogestion(e.target.checked)}
              />
              <span>Permitir que el paciente reserve turnos online</span>
            </label>

            <div className="config-grid-2">
              <div>
                <label className="config-field-label" htmlFor="landing-titulo">
                  Título de la landing
                </label>
                <input
                  id="landing-titulo"
                  type="text"
                  className="config-input"
                  value={landingTitulo}
                  onChange={(e) => setLandingTitulo(e.target.value)}
                  placeholder="Reservá tu turno"
                  maxLength={120}
                />
              </div>
              <div>
                <label className="config-field-label" htmlFor="landing-color">
                  Color de acento
                </label>
                <input
                  id="landing-color"
                  type="color"
                  className="config-input"
                  value={landingColor || "#0A1B3D"}
                  onChange={(e) => setLandingColor(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="config-field-label" htmlFor="landing-subtitulo">
                Subtítulo
              </label>
              <input
                id="landing-subtitulo"
                type="text"
                className="config-input"
                value={landingSubtitulo}
                onChange={(e) => setLandingSubtitulo(e.target.value)}
                placeholder="Elegí profesional, servicio y horario disponible."
                maxLength={255}
              />
            </div>

            <div>
              <label className="config-field-label" htmlFor="wa-bot-phone">
                Número WhatsApp del bot (para link wa.me)
              </label>
              <input
                id="wa-bot-phone"
                type="tel"
                className="config-input"
                value={waBotPhone}
                onChange={(e) => setWaBotPhone(e.target.value)}
                placeholder="5493518520205"
                maxLength={30}
              />
            </div>

            <p className="config-muted">
              URL pública: <code>{`${window.location.origin}/reservar`}</code>
            </p>

            <div className="modal-historia-buttons">
              <button
                type="button"
                className="config-save-btn"
                onClick={guardarAutogestion}
                disabled={savingAutogestion}
              >
                {savingAutogestion ? "Guardando…" : "Guardar autogestión"}
              </button>
            </div>
          </div>
        </section>
        ) : null}

        <section id="config-whatsapp" className="config-section">
          <h2>WhatsApp</h2>
          <p className="configuracion-subtitle">
            {waMode === "hub"
              ? "Número genérico compartido. Los mensajes salen desde el Hub central y las respuestas de pacientes llegan por webhook."
              : "Conexión vía Evolution API (Baileys) para confirmaciones y recordatorios de turnos."}
          </p>

          {waMode !== "hub" && !configured && !loading ? (
            <div className="config-alert error">
              Evolution API no está configurada en el servidor. Revisá las variables{" "}
              <code>EVOLUTION_API_*</code> en el backend.
            </div>
          ) : null}

          {error ? <div className="config-alert error">{error}</div> : null}

          <div className="config-card config-wa-status-card">
            {loading ? <p className="config-muted">Cargando estado...</p> : null}
            {!loading ? (
              <>
                <span className={`status-badge ${ready ? "connected" : "disconnected"}`}>
                  {ready ? "Conectado" : "Sin conectar"}
                </span>
                {waMode === "hub" ? (
                  <p className="config-muted config-wa-meta">
                    {ready
                      ? "Hub en línea y WhatsApp conectado. Las respuestas 1/2/3 de pacientes se enrutan a este consultorio."
                      : !hubOnline
                        ? "No se pudo contactar al Hub en Oracle. Revisá WA_HUB_URL y WA_HUB_TOKEN en el servidor."
                        : "Hub en línea, pero WhatsApp aún no está conectado en Evolution Manager."}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>

          {waMode === "direct" ? (
            <>
              <div className="config-wa-instructions" role="note">
                <h3>Cómo vincular tu WhatsApp</h3>
                <ol>
                  <li>
                    <strong>Abrí esta pantalla desde una computadora</strong> para escanear el código QR con comodidad.
                  </li>
                  <li>
                    En tu teléfono, abrí WhatsApp → <em>Dispositivos vinculados</em> → <em>Vincular dispositivo</em>.
                  </li>
                  <li>
                    <strong>Escaneá el QR de abajo</strong> (o usá el panel de Evolution si el QR no aparece aquí).
                  </li>
                  <li>
                    Configurá el webhook de Evolution apuntando a{" "}
                    <code>/api/webhooks/evolution</code> para recibir respuestas 1/2 del paciente.
                  </li>
                </ol>
              </div>

              <div className="config-card">
                {instanceName ? (
                  <p className="config-muted config-wa-meta">
                    Instancia: <strong>{instanceName}</strong>
                    {connectionState ? ` · Estado: ${connectionState}` : ""}
                  </p>
                ) : null}

                {!loading ? (
                  <div className="config-wa-queue-grid" aria-label="Estado de la cola de mensajes">
                    <div className="config-wa-queue-item">
                      <span>Pendientes</span>
                      <strong>{queueStats.pending ?? 0}</strong>
                    </div>
                    <div className="config-wa-queue-item">
                      <span>Enviando</span>
                      <strong>{queueStats.processing ?? 0}</strong>
                    </div>
                    <div className="config-wa-queue-item">
                      <span>Enviados</span>
                      <strong>{queueStats.sent ?? 0}</strong>
                    </div>
                    <div className="config-wa-queue-item">
                      <span>Fallidos</span>
                      <strong>{queueStats.failed ?? 0}</strong>
                    </div>
                  </div>
                ) : null}

                {!ready && !loading && qr ? (
                  <div className="qr-wrapper">
                    <p>Escaneá este QR con la app de WhatsApp del número del consultorio.</p>
                    <img src={qr} alt="QR de vinculación WhatsApp" />
                  </div>
                ) : null}

                {!ready && !loading && pairingCode ? (
                  <p className="config-muted">
                    Código de emparejamiento: <strong>{pairingCode}</strong>
                  </p>
                ) : null}

                {!ready && !loading && !qr ? (
                  <p className="config-muted">
                    No hay QR disponible todavía. Verificá que Evolution API esté corriendo y la instancia creada.
                  </p>
                ) : null}

                {managerUrl ? (
                  <a
                    className="config-wa-manager-link"
                    href={managerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir panel de Evolution
                  </a>
                ) : null}
              </div>
            </>
          ) : null}

          {selectedProfesionalId ? (
            <div className="config-card config-card-wide config-wa-prof">
              <h3 className="config-card-title">Identificación en mensajes</h3>
              <p className="config-muted">
                Configurá cómo se identifica el profesional en los WhatsApp enviados desde el número genérico.
                Al crear turnos, elegí el mismo profesional para que coincidan los datos.
              </p>

              {waConfigMsg.text ? (
                <div className={`config-alert ${waConfigMsg.type === "ok" ? "info" : "error"}`}>
                  {waConfigMsg.text}
                </div>
              ) : null}

              {showProfesionalSelector ? (
                <>
                  <label className="config-field-label" htmlFor="wa-profesional-select">
                    Profesional
                  </label>
                  <select
                    id="wa-profesional-select"
                    className="config-input config-select"
                    value={selectedProfesionalId}
                    onChange={(e) => setSelectedProfesionalId(Number(e.target.value))}
                    disabled={loadingWaConfig}
                  >
                    {profesionalesWa.map((p) => (
                      <option key={p.id} value={p.id}>
                        {profesionalLabel(p)}
                      </option>
                    ))}
                  </select>
                  <p className="config-muted config-input-hint">
                    Los turnos deben crearse con este mismo profesional para usar esta identificación.
                  </p>
                </>
              ) : null}

              <label className="config-field-label" htmlFor="wa-display-name">
                Nombre a mostrar en el mensaje
              </label>
              <input
                id="wa-display-name"
                type="text"
                className="config-input"
                placeholder="Ej: Dr. García - Odontología Integral"
                value={waDisplayName}
                onChange={(e) => setWaDisplayName(e.target.value)}
                disabled={loadingWaConfig}
                maxLength={120}
              />
              <p className="config-muted config-input-hint">
                Aparecerá en el mensaje como: <em>"Dr. García - Odontología Integral te recuerda tu turno…"</em>
              </p>

              <div className="config-wa-contact-group">
                <div>
                  <label className="config-field-label" htmlFor="wa-contact-name">
                    Nombre para tarjeta de contacto (opción 3)
                  </label>
                  <input
                    id="wa-contact-name"
                    type="text"
                    className="config-input"
                    placeholder="Ej: Dr. Carlos García"
                    value={waContactName}
                    onChange={(e) => setWaContactName(e.target.value)}
                    disabled={loadingWaConfig}
                    maxLength={120}
                  />
                </div>
                <div>
                  <label className="config-field-label" htmlFor="wa-contact-phone">
                    WhatsApp personal (para la tarjeta)
                  </label>
                  <input
                    id="wa-contact-phone"
                    type="tel"
                    className="config-input"
                    placeholder="Ej: 3513000000"
                    value={waContactPhone}
                    onChange={(e) => setWaContactPhone(e.target.value)}
                    disabled={loadingWaConfig}
                    maxLength={30}
                  />
                </div>
              </div>
              <p className="config-muted config-input-hint">
                La opción <strong>3</strong> solo aparece en el mensaje si cargás el WhatsApp personal.
                Si el paciente la elige, recibirá tu tarjeta de contacto.
              </p>

              <div className="modal-historia-buttons">
                <button
                  type="button"
                  className="config-save-btn"
                  onClick={guardarWaConfig}
                  disabled={savingWaConfig || loadingWaConfig}
                >
                  {savingWaConfig ? "Guardando…" : "Guardar configuración WhatsApp"}
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <section id="config-notificaciones" className="config-section config-section-last">
          <h2>Notificaciones push</h2>
          <p className="configuracion-subtitle">
            Recibí alertas en este celular o computadora cuando un paciente confirma, cancela o pide hablar con vos por WhatsApp.
          </p>

          {!pushSupported ? (
            <div className="config-alert error">
              Este navegador no soporta notificaciones push. Probá con Chrome, Firefox, Edge o Safari actualizado.
            </div>
          ) : null}

          {pushSupported && !pushConfigured && !pushLoading ? (
            <div className="config-alert error">
              El servidor aún no tiene claves VAPID configuradas. Ejecutá{" "}
              <code>php artisan webpush:vapid-keys</code> en el backend y agregá las variables al <code>.env</code>.
            </div>
          ) : null}

          {isIosDevice() && !isStandalonePwa() ? (
            <div className="config-wa-instructions" role="note">
              <h3>iPhone / iPad</h3>
              <ol>
                <li>Abrí TurnoOnline en Safari.</li>
                <li>Tocá <strong>Compartir</strong> → <strong>Agregar a inicio</strong>.</li>
                <li>Ingresá desde el ícono en la pantalla de inicio y activá las notificaciones acá.</li>
              </ol>
            </div>
          ) : null}

          {pushMsg.text ? (
            <div className={`config-alert ${pushMsg.type === "ok" ? "info" : "error"}`}>
              {pushMsg.text}
            </div>
          ) : null}

          <div className="config-card config-card-wide">
            {pushLoading ? <p className="config-muted">Consultando estado…</p> : null}

            {!pushLoading && pushSupported ? (
              <>
                <span className={`status-badge ${pushSubscribed ? "connected" : "disconnected"}`}>
                  {pushSubscribed ? "Activas en este dispositivo" : "Sin activar"}
                </span>

                {!pushSubscribed && pushOtherDevices > 0 ? (
                  <p className="config-muted config-wa-meta">
                    Tenés notificaciones activas en {pushOtherDevices === 1 ? "otro dispositivo" : `${pushOtherDevices} dispositivos`}.
                    Activá acá para recibirlas también en este celular o navegador.
                  </p>
                ) : null}

                {pushPermission === "denied" ? (
                  <p className="config-muted config-wa-meta">
                    Bloqueaste las notificaciones. Habilitalas desde la configuración del navegador o del sistema.
                  </p>
                ) : (
                  <p className="config-muted config-wa-meta">
                    Cada celular o computadora se activa por separado. Funciona en Android, iPhone (desde la app en pantalla de inicio) y navegadores de escritorio.
                  </p>
                )}

                {showProfesionalSelector ? (
                  <>
                    <label className="config-field-label" htmlFor="push-profesional-select">
                      Profesional a notificar
                    </label>
                    <select
                      id="push-profesional-select"
                      className="config-input config-select"
                      value={pushProfesionalId || selectedProfesionalId || ""}
                      onChange={(e) => setPushProfesionalId(Number(e.target.value))}
                      disabled={pushSaving}
                    >
                      {profesionalesWa.map((p) => (
                        <option key={p.id} value={p.id}>
                          {profesionalLabel(p)}
                        </option>
                      ))}
                    </select>
                    <p className="config-muted config-input-hint">
                      Recibirás alertas de los turnos asignados a este profesional. Elegí el mismo profesional con el que creás los turnos.
                    </p>
                  </>
                ) : null}

                <div className="modal-historia-buttons">
                  {pushSubscribed ? (
                    <button
                      type="button"
                      className="config-save-btn config-save-secondary"
                      onClick={desactivarPush}
                      disabled={pushSaving || !pushConfigured}
                    >
                      {pushSaving ? "Procesando…" : "Desactivar en este dispositivo"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="config-save-btn"
                      onClick={activarPush}
                      disabled={
                        pushSaving
                        || !pushConfigured
                        || pushPermission === "denied"
                        || !(pushProfesionalId || selectedProfesionalId || myProfesionalId)
                      }
                    >
                      {pushSaving ? "Activando…" : "Activar notificaciones"}
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}
